import { Directive, ViewChild, ElementRef, inject } from '@angular/core';
import { Stripe } from '@df/module-stripe/stripe.namespace';
import { Address } from '@df/session/api/address-book/address';
import { AbstractStripeCardComponent } from '@df/module-stripe/abstract-stripe-card.component';
import { AddressService } from '@df/session/api/address-book/address.service';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class StripeCardComponentDef extends AbstractStripeCardComponent<Stripe.ICardPaymentMethod> {
    /**
     * Selected card type model
     * @type {string | undefined}
     */
    selectedCardType: Stripe.CardTypes | string | undefined;

    protected addressService = inject(AddressService);

    /**
     * Card number container
     */
    @ViewChild('cardNumberContainer')
    protected cardNumberContainer?: ElementRef<any>;

    /**
     * Card expiry container
     */
    @ViewChild('cardExpiryContainer')
    protected cardExpiryContainer?: ElementRef<any>;

    /**
     * Card cvc container
     */
    @ViewChild('cardCvcContainer')
    protected cardCvcContainer?: ElementRef<any>;

    /**
     * Save card model
     * @type {boolean}
     */
    private _saveCard = false;

    /**
     * Last card number change event
     * @type {Stripe.ICardNumberElementChangeEvent}
     */
    private numberChangeEvent?: Stripe.ICardNumberElementChangeEvent;

    /**
     * Last card expiry change event
     * @type {Stripe.ICardPartElementChangeEvent}
     */
    private expiryChangeEvent?: Stripe.ICardPartElementChangeEvent;

    /**
     * Last card cvc change event
     * @type {Stripe.ICardPartElementChangeEvent}
     */
    private cvcChangeEvent?: Stripe.ICardPartElementChangeEvent;

    /**
     * Card number element
     * @type {Stripe.ICardNumberElementInstance}
     */
    private cardNumberElement?: Stripe.ICardNumberElementInstance;

    /**
     * Card expiry element
     * @type {Stripe.ICardExpiryElementInstance}
     */
    private cardExpiryElement?: Stripe.ICardExpiryElementInstance;

    /**
     * Card cvc element
     * @type {Stripe.ICardCvcElementInstance}
     */
    private cardCvcElement?: Stripe.ICardCvcElementInstance;

    /**
     * Save card setter
     * @param {boolean} - value;
     */
    set saveCard(value: boolean) {
        this._saveCard = value;
    }

    /**
     * Save card getter
     * @returns {boolean}
     */
    get saveCard(): boolean {
        return this.useSaveCard() && this._saveCard;
    }

    /**
     * If we should display save card checkbox in component
     * @returns {boolean}
     */
    useSaveCard(): boolean {
        return this.app.customer.model.isLoggedIn;
    }

    /**
     * Checks if elements can be displayed
     * @returns {boolean}
     */
    protected canDisplayElements(): boolean {
        return !!(
            this.cardNumberContainer?.nativeElement &&
            this.cardExpiryContainer?.nativeElement &&
            this.cardCvcContainer?.nativeElement
        );
    }

    /**
     * Returns if we should use setup intents flow
     * @returns {boolean}
     */
    private get useSetupIntents(): boolean {
        return !!this.method?.config?.initParams?.useSetupIntents;
    }

    //////////////////////////////////////////
    /////
    /////   Stripe methods
    /////
    //////////////////////////////////////////

    /**
     * On elements object created callback
     */
    protected onElementsCreated(): void {
        const cardNumberContainer = this.cardNumberContainer?.nativeElement;
        const cardExpiryContainer = this.cardExpiryContainer?.nativeElement;
        const cardCvcContainer = this.cardCvcContainer?.nativeElement;
        if (cardNumberContainer && cardExpiryContainer && cardCvcContainer && this.elements) {
            this.cardNumberElement = this.initElement(Stripe.Component.CardNumber, this.getCardNumberOptions(), cardNumberContainer, e =>
                this.onCardNumberChange(e)
            );
            this.cardExpiryElement = this.initElement(Stripe.Component.CardExpiry, this.getCardExpiryOptions(), cardExpiryContainer, e =>
                this.onCardExpiryChange(e)
            );
            this.cardCvcElement = this.initElement(Stripe.Component.CardCvc, this.getCardCvcOptions(), cardCvcContainer, e =>
                this.onCardCvcChange(e)
            );
        }
    }

    /**
     * Initiate card element
     * @param {Stripe.Component}                                type
     * @param {Stripe.ICreateOptions}                           options
     * @param {string | HTMLElement}                            container
     * @param {(event: Stripe.IElementChangeEvent) => void}     onChange
     * @returns {Stripe.IElementInstance}
     */
    private initElement(
        type: Stripe.Component,
        options: Stripe.ICreateOptions,
        container: string | HTMLElement,
        onChange?: (event: Stripe.IElementChangeEvent) => void
    ): Stripe.IElementInstance {
        if (!this.elements) {
            throw new Error('Stripe elements not initialized');
        }
        const element = this.elements.create(type, options);
        if (onChange) {
            element.on(Stripe.ElementEvent.Change, (event: Stripe.IElementChangeEvent) => onChange(event));
        }
        element.mount(container);
        return element;
    }

    /**
     * On submitted change callback
     */
    protected override onSubmittedChange(): void {
        if (this.cardNumberElement) {
            this.cardNumberElement.update(this.submitted ? this.getSubmittedCardNumberOptions() : this.getCardNumberOptions());
        }

        if (this.cardExpiryElement) {
            this.cardExpiryElement.update(this.submitted ? this.getSubmittedCardExpiryOptions() : this.getCardExpiryOptions());
        }

        if (this.cardCvcElement) {
            this.cardCvcElement.update(this.submitted ? this.getSubmittedCardCvcOptions() : this.getCardCvcOptions());
        }
    }

    /**
     * Returns card number element options
     * @returns {Stripe.ICreateCardNumberOptions}
     */
    protected getCardNumberOptions(): Stripe.ICreateCardNumberOptions {
        return {};
    }

    /**
     * Returns card expiry element options
     * @returns {Stripe.ICreateCardExpiryOptions}
     */
    protected getCardExpiryOptions(): Stripe.ICreateCardExpiryOptions {
        return {};
    }

    /**
     * Returns card cvc element options
     * @returns {Stripe.ICreateCardCvcOptions}
     */
    protected getCardCvcOptions(): Stripe.ICreateCardCvcOptions {
        return {};
    }

    /**
     * Returns card number element options when submitted
     * @returns {Stripe.ICreateCardNumberOptions}
     */
    protected getSubmittedCardNumberOptions(): Stripe.ICreateCardNumberOptions {
        return {};
    }

    /**
     * Returns card expiry element options when submitted
     * @returns {Stripe.ICreateCardExpiryOptions}
     */
    protected getSubmittedCardExpiryOptions(): Stripe.ICreateCardExpiryOptions {
        return {};
    }

    /**
     * Returns card cvc element options when submitted
     * @returns {Stripe.ICreateCardCvcOptions}
     */
    protected getSubmittedCardCvcOptions(): Stripe.ICreateCardCvcOptions {
        return {};
    }

    /**
     * On card numer change callback
     * @param {Stripe.ICardNumberElementChangeEvent} event
     */
    protected onCardNumberChange(event: Stripe.ICardNumberElementChangeEvent): void {
        this.numberChangeEvent = event;
        this.selectedCardType = event?.brand;
        this.onChange();
    }

    /**
     * On card expiry change callback
     * @param {Stripe.ICardPartElementChangeEvent} event
     */
    protected onCardExpiryChange(event: Stripe.ICardPartElementChangeEvent): void {
        this.expiryChangeEvent = event;
        this.onChange();
    }

    /**
     * On card cvc change callback
     * @param {Stripe.ICardPartElementChangeEvent} event
     */
    protected onCardCvcChange(event: Stripe.ICardPartElementChangeEvent): void {
        this.cvcChangeEvent = event;
        this.onChange();
    }

    /**
     * On change callback
     */
    private onChange(): void {
        this.valid = this.isValid();
        this.detectChanges();
    }

    /**
     * Returns if all elements are valid
     * @returns {boolean}
     */
    private isValid(): boolean {
        if (this.cvcChangeEvent && this.expiryChangeEvent && this.numberChangeEvent) {
            return ![this.cvcChangeEvent, this.expiryChangeEvent, this.numberChangeEvent].find(i => i.empty || !i.complete || i.error);
        }
        return false;
    }

    /**
     * Clears data before creating new elements
     */
    protected override clear() {
        this.cardNumberElement?.destroy();
        this.cardNumberElement = undefined;
        this.cardExpiryElement?.destroy();
        this.cardExpiryElement = undefined;
        this.cardCvcElement?.destroy();
        this.cardCvcElement = undefined;
        this.numberChangeEvent = undefined;
        this.expiryChangeEvent = undefined;
        this.cvcChangeEvent = undefined;
        this.selectedCardType = undefined;
        this.valid = false;
        super.clear();
    }

    /**
     * Returns if component is ready
     * @returns {boolean}
     */
    protected isComponentReady(): boolean {
        return !!(this.cardNumberElement && this.cardExpiryElement && this.cardCvcElement);
    }

    //////////////////////////////////////////
    /////
    /////   Stripe payment methods
    /////
    //////////////////////////////////////////

    /**
     * Returns data for client secret request
     * @returns {Stripe.IGetClientSecretData}
     */
    private getLoadClientSecretData(): Stripe.IGetClientSecretData {
        return {
            customer_data: {
                billingAddress: this.checkoutFormComponent.billingAddress!,
                guestEmail: this.checkoutFormComponent.email
            }
        };
    }

    /**
     * Returns create pamyment method data
     * @returns {Stripe.ICreatePaymentMethodData | undefined}
     */
    private getCreatePaymentMethodData(): Stripe.ICreatePaymentMethodData | undefined {
        const cardIntentData = this.getCardIntentData();
        if (cardIntentData) {
            return { ...cardIntentData, type: Stripe.PaymentType.Card };
        }
        return undefined;
    }

    /**
     * Returns card intent data
     * @returns {Stripe.ICardIntentData | undefined}
     */
    private getCardIntentData(): Stripe.ICardIntentData | undefined {
        if (this.cardNumberElement) {
            const data: Stripe.ICardIntentData = { card: this.cardNumberElement };
            const billingDetails = this.getBillingDetails();
            if (billingDetails) {
                data.billing_details = billingDetails;
            }
            return data;
        }
        return undefined;
    }

    /**
     * Returns billing details
     * @returns {Stripe.IBillingDetails | undefined}
     */
    private getBillingDetails(): Stripe.IBillingDetails | undefined {
        const billingAddress = this.checkoutFormComponent.billingAddress;
        if (billingAddress) {
            const billingDetails: Stripe.IBillingDetails = {
                name: [billingAddress.firstname, billingAddress.lastname].filter(i => !!i).join(' '),
                email: this.checkoutFormComponent.email,
                phone: billingAddress.telephone,
                address: {
                    city: billingAddress.city,
                    country: billingAddress.country_id,
                    line1: billingAddress.street_1,
                    postal_code: billingAddress.postcode
                }
            };
            if (billingAddress.street_2) {
                billingDetails.address!.line2 = billingAddress.street_2;
            }
            const region = this.addressService.getRegionFromAddress(billingAddress);
            if (region) {
                billingDetails.address!.state = region;
            }
            return billingDetails;
        }
        return undefined;
    }

    /**
     * Gets token from card elements
     * @returns {Promise<string>}
     */
    protected getToken(): Promise<string> {
        return new Promise((resolve, reject) => {
            if (this.useSetupIntents) {
                this.stripeService.getClientSecret(
                    undefined,
                    this.getLoadClientSecretData(),
                    (response: Stripe.IGetClientSecretResponse) => {
                        const clientSecret = response.client_secret;
                        if (clientSecret) {
                            this.stripe!.confirmCardSetup(clientSecret, { payment_method: this.getCardIntentData()! })
                                .then(response => {
                                    if (response?.error || !response?.setupIntent) {
                                        reject(response?.error?.message);
                                    } else {
                                        resolve(response.setupIntent.payment_method + '::');
                                    }
                                })
                                .catch(reject);
                        } else {
                            reject();
                        }
                    },
                    reject
                );
            } else {
                const createPaymentMethodData = this.getCreatePaymentMethodData();
                if (createPaymentMethodData) {
                    this.stripe!.createPaymentMethod<Stripe.ICardPaymentMethod>(createPaymentMethodData)
                        .then(response => {
                            if (response?.error || !response?.paymentMethod) {
                                reject(response?.error?.message);
                            } else {
                                resolve(
                                    [response.paymentMethod.id, response.paymentMethod.card.brand, response.paymentMethod.card.last4].join(
                                        ':'
                                    )
                                );
                            }
                        })
                        .catch(reject);
                } else {
                    reject();
                }
            }
        });
    }

    //////////////////////////////////////////
    /////
    /////   Errors
    /////
    //////////////////////////////////////////

    /**
     * Returns card number element error
     * @returns {string}
     */
    get cardNumberError(): string {
        return this.numberChangeEvent ? this.getFieldError(this.numberChangeEvent) : '';
    }

    /**
     * Returns card expiry element error
     * @returns {string}
     */
    get cardExpiryError(): string {
        return this.expiryChangeEvent ? this.getFieldError(this.expiryChangeEvent) : '';
    }

    /**
     * Returns card number cvc error
     * @returns {string}
     */
    get cardCvcError(): string {
        return this.cvcChangeEvent ? this.getFieldError(this.cvcChangeEvent) : '';
    }

    /**
     * Returns error message from card element change event
     * @param {Stripe.ICardPartElementChangeEvent} changeEvent
     * @returns {string}
     */
    private getFieldError(changeEvent: Stripe.ICardPartElementChangeEvent): string {
        if (changeEvent && !changeEvent.empty) {
            return changeEvent?.error?.message || '';
        }
        if (this.submitted) {
            return 'This field is required';
        }
        return '';
    }

    /**
     * Returns if card number is complete
     * @returns {boolean}
     */
    get cardNumberComplete(): boolean {
        return this.numberChangeEvent?.complete || false;
    }

    /**
     * Returns if card expiry is complete
     * @returns {boolean}
     */
    get cardExpiryComplete(): boolean {
        return this.expiryChangeEvent?.complete || false;
    }

    /**
     * Returns if card cvc is complete
     * @returns {boolean}
     */
    get cardCvcComplete(): boolean {
        return this.cvcChangeEvent?.complete || false;
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * Returns checkout submission payment data
     * @param {string} token
     * @returns {Stripe.ICardSubmissionData}
     */
    protected override getSubmissionPaymentData(token: string): Stripe.ICardSubmissionData {
        const superData = super.getSubmissionPaymentData(token);
        if (this.saveCard) {
            superData.additional_data.cc_save = true;
        }
        return superData;
    }
}
